/**
 * Drop Service - Frontend service for drop operations
 */

import * as dropRepository from "../repositories/dropRepository";
import { logError, logInfo } from "../../../shared/utils/logger.js";

export const getDropsByTrip = async (tripId) => {
  try {
    const response = await dropRepository.fetchDropsByTrip(tripId);
    if (!response.success)
      throw new Error(response.error || "Failed to fetch drops");
    return response.data;
  } catch (error) {
    logError("Failed to fetch drops", {
      tripId,
      error: error.message,
    });
    throw error;
  }
};

export const getDrop = async (dropId) => {
  try {
    const response = await dropRepository.fetchDropById(dropId);
    if (!response.success)
      throw new Error(response.error || "Failed to fetch drop");
    return response.data;
  } catch (error) {
    logError("Failed to fetch drop", {
      dropId,
      error: error.message,
    });
    throw error;
  }
};

export const createDrop = async (tripId, dropData) => {
  try {
    const response = await dropRepository.createDrop(tripId, dropData);
    if (!response.success)
      throw new Error(response.error || "Failed to create drop");
    logInfo("Drop created successfully", { dropId: response.data?.id });
    return response;
  } catch (error) {
    logError("Failed to create drop", {
      tripId,
      dropData,
      error: error.message,
    });
    throw error;
  }
};

export const updateDrop = async (dropId, updateData) => {
  try {
    const response = await dropRepository.updateDrop(dropId, updateData);
    if (!response.success)
      throw new Error(response.error || "Failed to update drop");
    return response;
  } catch (error) {
    logError("Failed to update drop", {
      dropId,
      updateData,
      error: error.message,
    });
    throw error;
  }
};

export const updateDropStatus = async (dropId, status) => {
  try {
    const response = await dropRepository.updateDropStatus(dropId, status);
    if (!response.success)
      throw new Error(response.error || "Failed to update drop status");
    return response.data;
  } catch (error) {
    logError("Failed to update drop status", {
      dropId,
      status,
      error: error.message,
    });
    throw error;
  }
};

export const completeDrop = async (dropId, confirmationData) => {
  try {
    const response = await dropRepository.completeDrop(dropId, confirmationData);
    if (!response.success)
      throw new Error(response.error || "Failed to complete drop");
    logInfo("Drop completed successfully", { dropId });
    return response.data;
  } catch (error) {
    logError("Failed to complete drop", {
      dropId,
      confirmationData,
      error: error.message,
    });
    throw error;
  }
};

export const addOrderToDrop = async (dropId, orderId, orderData = {}) => {
  try {
    const response = await dropRepository.addOrderToDrop(dropId, orderId, orderData);
    if (!response.success)
      throw new Error(response.error || "Failed to add order to drop");
    return response.data;
  } catch (error) {
    logError("Failed to add order to drop", {
      dropId,
      orderId,
      error: error.message,
    });
    throw error;
  }
};

export const updateOrderDeliveryProgress = async (dropId, orderId, itemsDelivered, options = {}) => {
  try {
    const response = await dropRepository.updateOrderDeliveryProgress(dropId, orderId, itemsDelivered, options);
    if (!response.success)
      throw new Error(response.error || "Failed to update delivery progress");
    logInfo("Delivery progress updated successfully", { dropId, orderId, itemsDelivered, skipEmail: options.skipEmail });
    return response.data;
  } catch (error) {
    logError("Failed to update delivery progress", {
      dropId,
      orderId,
      itemsDelivered,
      error: error.message,
    });
    throw error;
  }
};

export const removeOrderFromDrop = async (dropId, orderId) => {
  try {
    const response = await dropRepository.removeOrderFromDrop(dropId, orderId);
    if (!response.success)
      throw new Error(response.error || "Failed to remove order from drop");
    return response.data;
  } catch (error) {
    logError("Failed to remove order from drop", {
      dropId,
      orderId,
      error: error.message,
    });
    throw error;
  }
};

export const deleteDrop = async (dropId) => {
  try {
    const response = await dropRepository.deleteDrop(dropId);
    if (!response.success)
      throw new Error(response.error || "Failed to delete drop");
    return response;
  } catch (error) {
    logError("Failed to delete drop", {
      dropId,
      error: error.message,
    });
    throw error;
  }
};

